import React, { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";

import AccountDetailsCard from "./card/accountDetailsCard";
import { ReactComponent as WarningIcon } from "../../images/risk.svg";
import Table from "../../ui-components/table/table";
import CustomCell, { CustomCellTypes } from "../../ui-components/table/custom-cells/customCell";
import Tag from "../../ui-components/molecules/tag/tag";
import { TagColor } from "../../app/data/common/tagColor";

import { claimsSelector, getClaimsList } from "../../slices/claims/claimsSlice";
import { CLAIM_REASON_TYPES, CLAIM_STATUS, CLAIM_STATUSES } from "../claims/constants";
import useWindowSize from "../../hooks/useWindowsSize";
import { ClaimListItem } from "../../slices/claims/ClaimsState";
import { accountDetailsSelector } from "../../slices/account-details/accountDetailsSlice";

const columnHelper = createColumnHelper<ClaimListItem>();

const AccountDetailsClaims: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const claimsState = useSelector(claimsSelector);
  const accountDetailsState = useSelector(accountDetailsSelector);
  const tableRef = useRef<HTMLDivElement>(null);
  const windowWidth = useWindowSize()[0];

  const getStatusColor = (status: string) => {
    switch (status) {
      case CLAIM_STATUS.NEW:
        return TagColor.RED;
      case CLAIM_STATUS.APPROVED:
        return TagColor.GREEN;
      case CLAIM_STATUS.DECLINED:
        return TagColor.GREY;
      default:
        return TagColor.GREY
    }
  }
  
  const columns = [
    columnHelper.accessor("claimNumber", {
      header: "Claim #",
      size: 120,
    }),
    columnHelper.accessor("probill", {
      header: "Probill #",
      size: 100,
    }),
    columnHelper.accessor("reason", {
      header: "Reason",
      cell: (cellProps) => (
        <>
          {CLAIM_REASON_TYPES.find(reason => reason.value === cellProps.getValue())?.label}
        </>
      ),
      size: 222,
    }),
    columnHelper.accessor("createdAt", {
      header: "Date Created",
      cell: (cellProps) => (
        <>
          {cellProps.getValue().toUiDateFormat()}
        </>
      ),
      size: 120,
    }),
    columnHelper.accessor("dateResolved", {
      header: "Date Resolved",
      cell: (cellProps) => (
        <>
          {cellProps.getValue()?.toUiDateFormat() || "-"}
        </>
      ),
      size: 120,
    }),
    columnHelper.accessor("settlementAmount", {
      header: () => (
        <div className="text-right">
          Settlement Amount
        </div>
      ),
      cell: (cellProps) => (
        <div className="text-right">
          {cellProps.getValue()
            ? <CustomCell
              cellType={CustomCellTypes.amount}
              selector="settlementAmount"
              data={cellProps.row.original}
              value={cellProps.getValue() || "0"}
            />
            : <>-</>
          }
        </div>
      ),
      size: 120
    }),
    columnHelper.accessor("status", {
      header: () => (
        <div className="text-center">
          Status
        </div>
      ),
      cell: (cellProps) => (
        <div className="text-center">
          <Tag mods={{ color: getStatusColor(cellProps.getValue()), uppercase: true }}>
            {CLAIM_STATUSES.find(status => status.value === cellProps.getValue())?.label}
          </Tag>
        </div>
      ),
      size: 124,
    })
  ];

  //eslint-disable-next-line
  const contentWidth = useMemo(() => (tableRef.current && tableRef.current?.offsetWidth), [windowWidth]);

  const onInfiniteScroll = () => {
    dispatch(getClaimsList({ accountNumber: accountDetailsState.companyInfo?.accountId }, claimsState.claimsRequest.lastIds));
  }

  return (
    <AccountDetailsCard
      mix="account-details__claims"
      title="Claims"
      icon={<WarningIcon />}
      scrollable={true}
      contentWidth={contentWidth}
    >
      <div className="account-details__table-wrapper">
        <Table
          ref={tableRef}
          isLoading={claimsState.requestStarted && claimsState.requestCreator === "GET_CLAIMS"}
          columns={columns}
          data={claimsState.claims}
          cursorPointer={true}
          rowHeight={56}
          minTableHeight={200}
          responsive={true}
          infiniteScroll={true}
          infiniteScrollLoading={claimsState.requestPortionStarted}
          infiniteScrollHasNext={!claimsState.claimsFetchedAll}
          onInfiniteScroll={onInfiniteScroll}
        />
      </div>
    </AccountDetailsCard>
  );
};

export default AccountDetailsClaims;